import { DashboardCell, DashboardConfig, DashboardLayout } from '../types/gotypes'

/**
 * The layout of the page currently being edited/viewed.
 *
 * Falls back to the first page (an index can outlive the page it pointed at)
 * and then to the legacy root layout for single-layout dashboards. Shared so
 * the editor and the async paste handlers resolve "the active layout" the same
 * way — the handlers must re-derive it from live state after an await.
 */
export function resolveActiveLayout(config: DashboardConfig, activePageIndex: number): DashboardLayout {
  const pages = config.pages || []
  const page = pages[activePageIndex] || pages[0]
  return page?.layout || config.layout || { rows: [] }
}

/**
 * The id of the page currently being edited, or `undefined` for a legacy
 * single-layout dashboard that has no pages.
 *
 * An async handler must capture this BEFORE it awaits: a page *index* stops
 * meaning the same page the moment the user switches tab, reorders pages or
 * deletes one, all of which they can do while a paste forks its multi-tables.
 */
export function activePageId(config: DashboardConfig, activePageIndex: number): string | undefined {
  const pages = config.pages || []
  return (pages[activePageIndex] || pages[0])?.id
}

/**
 * The current layout of the page `pageId` identifies.
 *
 * `null` means that page is gone — the caller must report that and commit
 * nothing, rather than writing its layout onto whichever page now sits at the
 * old index. `pageId === undefined` is the legacy no-pages dashboard, whose
 * single root layout cannot go away.
 */
export function resolvePageLayout(config: DashboardConfig, pageId: string | undefined): DashboardLayout | null {
  const pages = config.pages || []
  if (pages.length === 0) return config.layout || { rows: [] }
  if (pageId === undefined) return null
  return pages.find(p => p.id === pageId)?.layout || null
}

/**
 * Calculate the actual widths for cells in a row.
 * - Cells with explicit widths use those values
 * - Remaining space is distributed equally among cells without widths
 *
 * @param cells Array of dashboard cells
 * @returns Array of calculated widths (as percentages)
 */
export function calculateCellWidths(cells: DashboardCell[]): number[] {
  if (cells.length === 0) return []

  // Count cells with explicit widths and sum their percentages
  let totalExplicitWidth = 0
  let cellsWithoutWidth = 0

  cells.forEach(cell => {
    if (cell.display_props?.width !== undefined) {
      totalExplicitWidth += cell.display_props.width
    } else {
      cellsWithoutWidth++
    }
  })

  // Calculate remaining percentage
  const remainingWidth = Math.max(0, 100 - totalExplicitWidth)

  // Calculate width for cells without explicit width
  const autoWidth = cellsWithoutWidth > 0 ? remainingWidth / cellsWithoutWidth : 0

  // Build the result array
  return cells.map(cell => {
    if (cell.display_props?.width !== undefined) {
      return cell.display_props.width
    }
    return autoWidth
  })
}
